import { Card } from "shared/components/Card";
import { ApplicantSettings } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import { CriteriaTypeOptions } from "shared/app/VerificationTemplate/slices/DecisionCriteria/constants";
import {
  RecommendationMethod,
  GroupStat
} from "types/landlord/verificationTemplate";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { useState } from "react";
import { Loader } from "shared/components/Loader";
import { Applicant } from "types/application";
import { ApplicantType } from "shared/types";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Hide,
  ImageSkeleton,
  Show
} from "shared/components";
import { EditEstimatedMonthlyPaymentsModal } from "./components/EditEstimatedMonthlyPaymentsModal";
import { ConfigureMetricsDrawer } from "./components/ConfigureMetricsDrawer";
import {
  Analytics01Icon,
  DashboardSquare01Icon,
  Layout04Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { MetricCalculationModal } from "./components/MetricCalculationModal";
import { LegacyGroupStat } from "./components/LegacyGroupStat";
import { MetricWidget } from "./components/MetricWidget";
import { useConfigurableGroupMetric } from "./hooks/use-configurable-group-metric";
import { ConfirmResetMetricsModal } from "./components/ConfirmResetMetricsModal";
import { has } from "shared/utils";
import { ApplicationMetricData } from "api/applications";

type ConfigureDrawerState =
  | { mode: "add"; metric?: null }
  | { mode: "edit"; metric: ApplicationMetricData }
  | null;

export const GroupStats = ({
  criteriaTypeOptions,
  criteriaSettings,
  recommendationMethod,
  groupStat,
  includeCoSignerInCriteriaCalculation,
  applicationId,
  applicants,
  hasConfigurableGroupStat = false
}: {
  criteriaTypeOptions: CriteriaTypeOptions;
  criteriaSettings?: ApplicantSettings;
  recommendationMethod?: RecommendationMethod;
  groupStat: GroupStat;
  isRuleBased?: boolean;
  includeCoSignerInCriteriaCalculation?: boolean;
  applicationId: string;
  applicants?: Applicant[];
  hasConfigurableGroupStat?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [showEditEmpModal, setShowEditEmpModal] = useState(false);
  const [includeCoSigner, setIncludeCoSigner] = useState<boolean>(
    includeCoSignerInCriteriaCalculation || false
  );

  const { mutate: handleIncludeCoSigner, isPending: includeCoSignerPending } =
    useMutation({
      mutationKey: ["APPLICATION", applicationId],
      mutationFn: () => {
        return instance.patch(
          `portal/v1/applications/${applicationId}/include_co_signer`,
          {
            include_co_signer: !includeCoSigner
          }
        );
      },
      onSuccess: () => {
        setIncludeCoSigner(!includeCoSigner);
        queryClient.invalidateQueries({
          queryKey: ["APPLICATION", applicationId]
        });
      },
      onError: (err) => {
        toast.error(getErrorMessage(err.message));
      }
    });

  const [configureDrawerState, setConfigureDrawerState] =
    useState<ConfigureDrawerState>(null);
  const [showMetricBreakdownModal, setShowMetricBreakdownModal] =
    useState<ApplicationMetricData | null>(null);
  const [showConfirmResetModal, setShowConfirmResetModal] = useState(false);

  const { metricsData, isMetricsPending, isResetting } =
    useConfigurableGroupMetric(hasConfigurableGroupStat);
  const metricWidgets = metricsData?.metrics ?? [];
  const hasCustomTemplate = Boolean(metricsData?.template);

  // Guard for legacy group stats
  const hasCreditScore = Boolean(groupStat.max_credit_score);
  const hasIncome = Boolean(
    has(groupStat.gross_income) ||
    has(groupStat.net_income) ||
    has(groupStat.self_reported_gross_income)
  );
  if (!hasConfigurableGroupStat) {
    if (!Object.values(groupStat).filter((v) => Boolean(v)).length) return null;
    if (!hasCreditScore && !hasIncome) return null;
  }

  const showHeader =
    hasConfigurableGroupStat || (!hasConfigurableGroupStat && hasIncome);

  return (
    <Card className="w-full flex flex-col max-w-[1500px]">
      <Show if={showHeader}>
        <header className="flex items-center justify-between px-6 py-4 border-b border-alpha-40">
          <h3 className="text-base font-medium text-content-700">
            Group metrics
          </h3>
          <div className="flex items-center gap-4">
            <Hide if={hasConfigurableGroupStat}>
              {includeCoSignerPending ? (
                <Loader size={25} />
              ) : (
                <Checkbox
                  wrapperClassName="row-reverse"
                  label="Include guarantors"
                  disabled={
                    applicants?.filter(
                      (applicant) => applicant.role === ApplicantType.CoSigner
                    ).length === 0
                  }
                  checked={Boolean(includeCoSigner)}
                  onChange={() => handleIncludeCoSigner()}
                />
              )}
            </Hide>

            <Show if={hasConfigurableGroupStat}>
              <Show if={hasCustomTemplate}>
                <Button
                  className="!p-4 !gap-2"
                  onClick={() => setShowConfirmResetModal(true)}
                  disabled={isResetting}
                  HugeIcon={Layout04Icon}
                  iconClassName="text-content-1000"
                  iconStrokeWidth={1.5}
                  size={ButtonSize.SM}
                >
                  Reset to default
                </Button>
              </Show>
              <Button
                className="!p-4 !gap-2"
                onClick={() => setConfigureDrawerState({ mode: "add" })}
                HugeIcon={DashboardSquare01Icon}
                iconClassName="text-content-1000"
                iconStrokeWidth={1.5}
                size={ButtonSize.SM}
              >
                Edit view
              </Button>
            </Show>
          </div>
        </header>
      </Show>

      <Hide if={hasConfigurableGroupStat}>
        <LegacyGroupStat
          groupStat={groupStat}
          criteriaSettings={criteriaSettings}
          recommendationMethod={recommendationMethod}
          criteriaTypeOptions={criteriaTypeOptions}
          onEditEmp={() => setShowEditEmpModal(true)}
          hasIncome={hasIncome}
        />
      </Hide>

      <Show if={hasConfigurableGroupStat}>
        <Show if={isMetricsPending}>
          <div className="grid [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))] px-4 py-6">
            {new Array(12).fill("_").map((__, idx) => (
              <ImageSkeleton
                key={`skeleton-${idx}`}
                className="rounded-lg h-32 m-2"
              />
            ))}
          </div>
        </Show>
        <Hide if={isMetricsPending}>
          {metricWidgets.length > 0 ? (
            <div className="grid [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))] px-4 py-6">
              {metricWidgets.map((metricWidget) => (
                <MetricWidget
                  key={`${metricWidget.index}-${metricWidget.metric}`}
                  metricData={metricWidget}
                  editMetric={(metric: ApplicationMetricData) =>
                    setConfigureDrawerState({
                      mode: "edit",
                      metric: metric
                    })
                  }
                  setShowEditEmpModal={setShowEditEmpModal}
                  setShowMetricBreakdownModal={setShowMetricBreakdownModal}
                />
              ))}
            </div>
          ) : (
            <div className="h-96 flex flex-col items-center justify-center gap-2">
              <div className="rounded-full bg-primary-20 p-4 w-24 h-24 flex mb-5 -mt-2">
                <HugeiconsIcon
                  icon={Analytics01Icon}
                  size={40}
                  className="text-primary-500 m-auto"
                />
              </div>
              <p className="text-content-500 font-bold">
                Get a clear picture of application group
              </p>
              <p className="text-content-300 text-sm font-medium">
                Add metrics for applicants, occupants and guarantors
              </p>
              <Button
                variant={ButtonVariant.PRIMARY}
                className="mt-5"
                onClick={() => setConfigureDrawerState({ mode: "add" })}
              >
                Add metric
              </Button>
            </div>
          )}
        </Hide>
      </Show>

      <ConfigureMetricsDrawer
        opened={Boolean(configureDrawerState)}
        onClose={() => setConfigureDrawerState(null)}
        existingMetric={configureDrawerState?.metric}
      />
      <Show if={showMetricBreakdownModal}>
        <MetricCalculationModal
          metricData={showMetricBreakdownModal}
          onClose={() => setShowMetricBreakdownModal(null)}
        />
      </Show>
      <Show if={showConfirmResetModal}>
        <ConfirmResetMetricsModal
          opened={showConfirmResetModal}
          onClose={() => setShowConfirmResetModal(false)}
        />
      </Show>
      <Show if={showEditEmpModal}>
        <EditEstimatedMonthlyPaymentsModal
          opened={showEditEmpModal}
          onClose={() => setShowEditEmpModal(false)}
          applicationId={applicationId}
        />
      </Show>
    </Card>
  );
};
